import axios from 'axios'
import { domains } from 'config/env'
import Auth0Manager from 'auth/utils/auth0Manager'
import { timezone } from 'utils/getTimeZone'
import {
  PROGRAM_TIER2_COURSES,
  getAllTier2CoursesForProgram,
} from 'store/reportsManager/constants'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
const baseApiAddress = `${domains.REPORTS_API}`

const PERIOD = 14
const PROGRAM = {
  HS: 'HS',
  K8: 'K8',
}

// Courses arrays for filtering - replaces program parameter
const K8_COURSES = [
  'GradeK',
  'Grade1',
  'Grade2',
  'Grade3',
  'Grade4',
  'Grade5',
  'Grade6',
  'Grade7',
  'Grade8',
]

const DWB_COURSES = [
  'DWBGradeK',
  'DWBGrade1',
  'DWBGrade2',
  'DWBGrade3',
  'DWBGrade4',
  'DWBGrade5',
  'DWBGrade6',
  'DWBGrade7',
  'DWBGrade8',
  'DWBGrade9',
  'DWBGrade10',
  'DWBGrade11',
  'DWBGrade12',
]

const HS_DWB_COURSES = ['DWBGrade9', 'DWBGrade10', 'DWBGrade11', 'DWBGrade12']

export const getLessonCompletionCourses = (program, toggleView) => {
  if (toggleView === TOGGLE_VIEW.SPECIALIZED_UNITS) {
    if (program === PROGRAM.K8) return [...DWB_COURSES]
    if (program === PROGRAM.HS) return [...HS_DWB_COURSES]
    return []
  }
  if (program === PROGRAM.K8) return [...K8_COURSES]
  return []
}

const api = (function(baseApiAddress, fakeout = false) {
  const _api = axios.create({
    baseUrl: baseApiAddress,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
    },
    timeout: 60000,
  })

  _api.interceptors.request.use(async request => {
    const token = await Auth0Manager.getAccessToken()
    request.headers.Authorization = `Bearer ${token}`
    return request
  })

  const GET_ALL_USERS = {
    query: `
      query getAllUsers {
        user_dims(distinct: user_id) {
          name
          email
        }
      }
    `,
  }

  const selaReportContentTrackFragment = `
    fragment contentTrack on ContentChildren {
      contentful_id
      display_title
      type
      users_in_progress
      users_not_started
      users_completed
      users_completed_ids
      users_in_progress_ids
      users_not_started_ids
      total_users
    }
    `

  const selaReportUserChildrenFragment = `
    fragment userChildren on UserChildren {
      contentful_id
      display_title
      type
      progress
      status
      done_at
      children_completed
      total_children
      sub_content_type
    }
    `

  const selaReportContentTracksQuery = `
    content_tracks {
      display_title
      content_track
      content_children {
        ...contentTrack
        content_children {
          ...contentTrack
          content_children {
            ...contentTrack
            content_children {
              ...contentTrack
              content_children {
                ...contentTrack
              }
            }
          }
        }
      }
    }
    `

  const selaReportUserDetailsQuery = `
    user_details {
      user_id
      name
      email
      license_status
      instance_id
      role
      content_track
      user_children {
        ...userChildren
        user_children {
          ...userChildren
          user_children {
            ...userChildren
            user_children {
              ...userChildren
              user_children {
                ...userChildren
              }
            }
          }
        }
      }
    }
    `

  const GET_SELA_REPORT_DATA = licenseId => ({
    query: `
    ${selaReportContentTrackFragment}
    ${selaReportUserChildrenFragment}
    
    query {
      sela_report(license_id: ${licenseId}) {
        license_id
        users_totals {
          total
          license_statuses {
            license_status
            total
          }
        }

        ${selaReportContentTracksQuery}
        ${selaReportUserDetailsQuery}
      }
    }
    `,
  })

  const GET_SELA_REPORT_CONTENT_TRACKS = licenseId => ({
    query: `
    ${selaReportContentTrackFragment}
    
    query {
      sela_report(license_id: ${licenseId}) {
        license_id
        users_totals {
          total
          license_statuses {
            license_status
            total
          }
        }

        ${selaReportContentTracksQuery}
      }
    }
    `,
  })

  const GET_SELA_REPORT_USERS_DETAILS = licenseId => ({
    query: `
    ${selaReportUserChildrenFragment}
    
    query {
      sela_report(license_id: ${licenseId}) {
        license_id
        ${selaReportUserDetailsQuery}
      }
    }
    `,
  })

  // school view
  const GET_K8_REPORT_SCHOOL_BY_SITE = (siteId, timezone, period = 7) => {
    const variables = {
      siteId,
      period,
      timezone,
      courses: [...K8_COURSES, ...DWB_COURSES],
    }
    return {
      query: `
      query SchoolView($siteId: Int!, $timezone: TimeZone, $period: Int, $courses: [CoursesReporting]) {
        SiteDetails(period: $period, siteId: $siteId, timeZone: $timezone, courses: $courses) {
          lastUpdatedAt
          period
          siteId
          totalClassesCount
          teachersCount
          administratorsCount
          siteName
          pendingInvitations
          numClassesWithCompletedLessonLastPeriod
          hasClassesWithCompletedLessonLastPeriod
          allGrades {
            gradeName
            classesCount
            numClassesWithCompletedLessonLastPeriod
            hasClassesWithCompletedLessonLastPeriod
          }
        }
        Curriculum(siteId: $siteId, period_in_days: $period, timeZone: $timezone, courses: $courses) {
          user_id
          class_id
          site_id
          curriculum
          last_lesson_done_lesson_number
          last_lesson_done_title
          last_lesson_done_on
          lessons_done
          total_lessons
          any_lesson_completed_in_period
          lesson_completion
          grade
          class_name
          instance
          is_finished
        }
        Users(siteId: $siteId) {
          user_id
          first_name
          last_name
        }
      }
    `,
      variables,
    }
  }

  const GET_ADMIN_K8_SITES_PAGINATED = (
    pageSize,
    pageNumber,
    orderBy,
    sortKey,
  ) => ({
    query: `
    query K8AdminSitePage($pageSize: Int!, $pageNumber: Int!, $orderBy: SortBy!, $sortKey: SortKey!, $courses: [CoursesReporting!]) {
      K8AdminSitesPaginated(pageSize: $pageSize, pageNumber: $pageNumber, orderBy: $orderBy, sortKey: $sortKey, courses: $courses) {
        totalCount
        totalPages
        currentPage
        sites {
          siteName
          siteId
          totalUsersCount
          usersWithClassesCount
          usersWithoutClassesCount
          usersWithClassesLastUpdated
          adminsPendingCount
          adminsAcceptedCount
          teachersPendingCount
          teachersAcceptedCount
          classesCreatedLastUpdated
          classesCreated {
            total
            gradeK
            grade1
            grade2
            grade3
            grade4
            grade5
            grade6
            grade7
            grade8
          }
        }
      }
    }
    `,
    variables: {
      pageSize,
      pageNumber,
      orderBy,
      sortKey,
      courses: K8_COURSES,
    },
  })

  const GET_ADMIN_K8_SITES_SUMMARY = () => ({
    query: `
      query AdminSitesSummary($courses: [CoursesReporting!]) {
        AdminSitesSummary(courses: $courses) {
          totalSitesCount
          withoutClassesTotalCount
          classes {
            lastUpdated
            total
            gradeK
            grade1
            grade2
            grade3
            grade4
            grade5
            grade6
            grade7
            grade8
          }
          users {
            lastUpdated
            totalCount
            administrators{
              activeCount
              pendingCount
            }
            teachers{
              activeCount
              pendingCount
            }
            withClassesCount
          }
        }
      }
  `,
    variables: {
      courses: K8_COURSES,
    },
  })

  const GET_HS_ADMIN_SNAPSHOT = adminEmail => {
    const variables = {
      adminEmail,
    }
    return {
      query: `
        query HsLaunchSnapshot($adminEmail: String!) {
          Schools (adminEmail: $adminEmail) {
            school_name
            site_id
          }
          Users(adminEmail: $adminEmail) {
            site_id
            role
            user_status
            user_id
          }
          SchoolPracticesOverall(adminEmail: $adminEmail) {
            ssapi_site_id
            schoolwide_practice_completion
            number_of_schoolwide_practices_marked_done
            number_of_users_with_schoolwide_practices
            total_number_of_schoolwide_practices
            educator_practice_completion
            number_of_educator_practices_marked_done
            total_number_of_educator_practices
            number_of_users_with_educator_practices
          }
          SchoolStudentActivitiesOverallCompletion(adminEmail: $adminEmail) {
            ssapi_site_id
            overall_number_of_student_activities_completed
            number_of_users_with_student_activities_enabled
          }
          SchoolTraining(adminEmail: $adminEmail) { 
            site_id
            schoolwide_training_kickoff_status
            schoolwide_training_kickoff_completed_date
            training_program_completion_percent
            training_program_users_completed
            training_program_users_in_progress
            training_program_users_not_started
          }
          LastUpdatedAt {
            last_updated_at
          }
          SchoolStudentActivitiesPathways(adminEmail: $adminEmail) {
            ssapi_site_id
            pathway_number
            num_g9_student_activities_completed_in_pathway
            num_g10_student_activities_completed_in_pathway
            num_g11_student_activities_completed_in_pathway
            num_g12_student_activities_completed_in_pathway
          }
          SchoolPracticesPathways(adminEmail: $adminEmail) {
            ssapi_site_id
            pathway_number
            total_num_school_practices
            num_school_practices_completed
            total_num_educator_practices
            num_educator_practices_completed
          }
        }
      `,
      variables,
    }
  }

  const GET_ADMIN_SELA_SITES_LIST = () => ({
    query: `
    query SelaAdminSitesList {
      SelaAdminSitesList {
        siteName
        licenseId
      }
    }
    `,
  })
  // grade view
  const GET_K8_REPORT_GRADE_BY_SITE = (
    siteId,
    gradeFilter,
    timezone,
    period = 7,
  ) => {
    const variables = {
      siteId,
      period,
      timezone,
      gradeFilter,
      courses: K8_COURSES,
    }
    return {
      query: `
      query GradeView($siteId: Int!, $timezone: TimeZone, $period: Int, $gradeFilter: Grade!, $courses: [CoursesReporting]) {
        SiteDetails(period: $period, siteId: $siteId, timeZone: $timezone, courses: $courses) {
          lastUpdatedAt
          period
          siteId
          allGrades {
            gradeName
            classesCount
            numClassesWithCompletedLessonLastPeriod
            hasClassesWithCompletedLessonLastPeriod
          }
          gradeDetails(filter: $gradeFilter) {
            classesCount
            gradeName
            hasClassesWithCompletedLessonLastPeriod
            numClassesWithCompletedLessonLastPeriod
            classes {
              classInstance
              className
              hasCompletedLessonLastPeriod
              lastLessonCompleted
              lastLessonCompletedDate
              lastLessonCompletedParent
              lastLessonCompletedTitle
              lessonCount
              lessonsCompleted
              lessonCountCore
              lessonsCompletedCore
              lessonCountUnit5
              lessonsCompletedUnit5
              teacherFirstName
              teacherLastName
            }
          }
        }
      }
    `,
      variables,
    }
  }

  // user lands on class details drawer, needs everything: grade view and class details
  const GET_K8_REPORT_GRADE_AND_CLASS_DETAILS_BY_SITE = (
    siteId,
    gradeFilter,
    classInstance,
    timezone,
    period = 7,
  ) => ({
    query: `
      query GradeAndClassDetailsView {
        ClassDetails(classInstance: "${classInstance}", siteId: ${siteId}) {
          classInstance
          gradeName
          className
          teacherFirstName
          teacherLastName
          lessonsCompleted
          lessonCount
          lessons {
            parent
            parentTitle
            lessonNumber
            lessonTitle
            lastViewedDate
            status
            completedDate
          }
        }
        SiteDetails(period: ${period}, siteId: ${siteId}, timeZone: "${timezone}") {
          lastUpdatedAt
          period
          siteId
          allGrades {
            gradeName
            classesCount
            numClassesWithCompletedLessonLastPeriod
            hasClassesWithCompletedLessonLastPeriod
          }
          gradeDetails(filter: ${gradeFilter}) {
            classesCount
            gradeName
            hasClassesWithCompletedLessonLastPeriod
            numClassesWithCompletedLessonLastPeriod
            classes {
              classInstance
              className
              hasCompletedLessonLastPeriod
              lastLessonCompleted
              lastLessonCompletedDate
              lastLessonCompletedParent
              lastLessonCompletedTitle
              lessonCountCore
              lessonsCompletedCore
              lessonCountUnit5
              lessonsCompletedUnit5
              lessonCount
              lessonsCompleted
              teacherFirstName
              teacherLastName
            }
          }
        }
      }
    `,
  })
  // user opens details for class
  const GET_K8_REPORT_CLASS_DETAILS_BY_SITE = (
    siteId,
    classInstance,
    timezone,
    period = 7,
  ) => ({
    query: `
      query ClassDetailsView {
        ClassDetails(classInstance: "${classInstance}", siteId: ${siteId}) {
          classInstance
          gradeName
          className
          teacherFirstName
          teacherLastName
          lessonsCompleted
          lessonCount
          lessons {
            parent
            parentTitle
            lessonNumber
            lessonTitle
            lastViewedDate
            status
            completedDate
          }
        }
        SiteDetails(period: ${period}, siteId: ${siteId}, timeZone: "${timezone}") {
          lastUpdatedAt
          period
          siteId
        }
      }
    `,
  })

  const GET_LESSON_COMPLETION_DATA = (
    adminEmail,
    siteId,
    program,
    k8ToggleView,
  ) => {
    const courses = getLessonCompletionCourses(program, k8ToggleView)
    const variables = {
      adminEmail,
      siteId,
      program,
      courses,
    }
    return {
      query: `
    query LessonCompletionWholeQuery($adminEmail: String, $siteId: Int, $program: Program, $courses: [CoursesReporting]) {
      Schools (adminEmail: $adminEmail, siteId: $siteId, program: $program, courses: $courses) {
        school_name
        classes_created
        classes_started
        schoolwide_lesson_completion_percentage
        site_id
        lessons_marked_done
        total_lessons
      }
      UnitSummary (adminEmail: $adminEmail, siteId: $siteId, program: $program, courses: $courses) {
        unit_number
        grade
        curriculum
        completion_percentage
        lessons_marked_done
        total_lessons
        site_id
        class_count
        classes_started
        classes_with_a_lesson_complete
      }
      LastUpdatedAt {
        last_updated_at
      }
    }
    `,
      variables,
    }
  }
  const GET_K8_PLAYBOOK_DATA = (adminEmail, siteId) => {
    const courses = K8_COURSES
    const variables = {
      adminEmail,
      siteId,
      courses,
    }
    return {
      query: `
        query PlaybookData($adminEmail: String, $siteId: Int, $courses: [CoursesReporting]) {
          Schools(adminEmail: $adminEmail, siteId: $siteId, courses: $courses) {
            school_name
            site_id
            classes_created
            lessons_marked_done
            schoolwide_lesson_completion_percentage
            total_lessons
          }
        }
      `,
      variables,
    }
  }

  const GET_HS_USER_PROGRESS_SNAPSHOT = (siteId, program, type) => {
    const variables = {
      program,
      siteId,
      type,
    }
    const activitiesQuery = {
      query: `
        query HsUserProgressSnapshot($siteId: Int, $program: Program) {
          LastUpdatedAt {
            last_updated_at
          }
          Schools (siteId: $siteId, program: $program) {
            site_id
            student_activities
          }
          Users(siteId: $siteId, program: $program) {
            first_name
            last_name
            user_id
            user_status
          }
          UsersProgramRoles (siteId: $siteId) {
            has_educator_practice
            has_schoolwide_practice
            has_student_activities
            ssapi_site_id
            ssapi_user_id
            subject_department
          }
          UsersPathwaysStudentActivity (siteId: $siteId) {
            ssapi_site_id
            ssapi_user_id
            pathway_number
            number_student_activities_completed
            collection_1_grade_9_marked_done_date
            collection_1_grade_9_status
            collection_1_grade_10_marked_done_date
            collection_1_grade_10_status
            collection_1_grade_11_marked_done_date
            collection_1_grade_11_status
            collection_1_grade_12_marked_done_date
            collection_1_grade_12_status
            collection_2_grade_9_marked_done_date
            collection_2_grade_9_status
            collection_2_grade_10_marked_done_date
            collection_2_grade_10_status
            collection_2_grade_11_marked_done_date
            collection_2_grade_11_status
            collection_2_grade_12_marked_done_date
            collection_2_grade_12_status
            collection_3_grade_9_marked_done_date
            collection_3_grade_9_status
            collection_3_grade_10_marked_done_date
            collection_3_grade_10_status
            collection_3_grade_11_marked_done_date
            collection_3_grade_11_status
            collection_3_grade_12_marked_done_date
            collection_3_grade_12_status
          }
        }
      `,
      variables,
    }
    const practicesQuery = {
      query: `
        query HsUserProgressSnapshot($siteId: Int, $program: Program, $type: String) {
          LastUpdatedAt {
            last_updated_at
          }
          Schools (siteId: $siteId, program: $program) {
            site_id
            student_activities
          }
          Users(siteId: $siteId, program: $program) {
            first_name
            last_name
            user_id
            user_status
          }
          UsersProgramRoles (siteId: $siteId) {
            has_educator_practice
            has_schoolwide_practice
            has_student_activities
            ssapi_site_id
            ssapi_user_id
            subject_department
          }
          UsersPathwaysPractices (siteId: $siteId, practiceType: $type) {
            collection_completed
            collection_count
            practice_type
            pathway_number
            ssapi_user_id
            collection_1_marked_done_date
            collection_1_raw_status
            collection_1_status
            collection_2_marked_done_date
            collection_2_raw_status
            collection_2_status
            collection_3_marked_done_date
            collection_3_raw_status
            collection_3_status
          }
        }
      `,
      variables,
    }

    return type === 'activity' ? activitiesQuery : practicesQuery
  }

  // eslint-disable-next-line no-unused-vars
  const GET_TIER2_DASHBOARD_DATA = (adminEmail, siteId, courses) => {
    const variables = {
      adminEmail,
      siteId,
      courses,
    }
    return {
      query: `
        query Tier2DashQuery($adminEmail: String, $siteId: Int, $courses: [CoursesReporting]) {
          UnitSummary(adminEmail: $adminEmail, siteId: $siteId, courses: $courses) {
            class_count
            grade
            lessons_marked_done
            site_id
            total_lessons
            unit_number
          }
          Schools(adminEmail: $adminEmail, siteId: $siteId, courses: $courses) {
            classes_created
            classes_started
            classes_with_no_lessons_completed
            school_name
            site_id
          }
          LastUpdatedAt {
            last_updated_at
          }
        }
      `,
      variables,
    }
  }

  const GET_SITE_USERS = siteId => ({
    query: `
      query GetSiteUsers {
        SiteUsers(siteId: ${siteId}) {
          users {
            email
            classCount
          }
        }
      }
    `,
  })

  // eslint-disable-next-line no-unused-vars
  const GET_TIER2_LESSON_COMPLETION_DATA = (siteId, courses) => {
    const variables = {
      siteId,
      courses,
    }
    return {
      query: `
        query Tier2LessonCompletionQuery($siteId: Int, $courses: [CoursesReporting]) {
          Units(siteId: $siteId, courses: $courses) {
            class_id
            instance
            lessons_marked_done
            unit_number
          }
          Users(siteId: $siteId) {
            email
            first_name
            last_logged_in
            last_name
            user_id
          }
          Classes(siteId: $siteId, courses: $courses) {
            class_id
            class_name
            instance
            lessons_done
            user_id
            is_finished
          }
          LastUpdatedAt {
            last_updated_at
          }
        }
      `,
      variables,
    }
  }

  return {
    getLessonCompletionData: async (
      adminEmail = null,
      siteId = null,
      program,
      k8ToggleView,
    ) => {
      return _api.post(
        baseApiAddress,
        GET_LESSON_COMPLETION_DATA(adminEmail, siteId, program, k8ToggleView),
      )
    },
    getAllUsers: async () => {
      return _api.post(baseApiAddress, GET_ALL_USERS)
    },
    getReportsData: async licenseId => {
      return _api.post(baseApiAddress, GET_SELA_REPORT_DATA(licenseId))
    },
    getK8PlaybookData: async (adminEmail, siteId) => {
      return _api.post(baseApiAddress, GET_K8_PLAYBOOK_DATA(adminEmail, siteId))
    },
    getSiteUsers: async siteId => {
      return _api.post(baseApiAddress, GET_SITE_USERS(siteId))
    },
    getK8ReportSchoolBySiteId: async siteId => {
      return _api.post(
        baseApiAddress,
        GET_K8_REPORT_SCHOOL_BY_SITE(siteId, timezone(), PERIOD),
      )
    },
    getAdminHsLaunchSnapshot: async adminEmail => {
      return _api.post(baseApiAddress, GET_HS_ADMIN_SNAPSHOT(adminEmail))
    },
    getAdminSelaSitesList: async () => {
      return _api.post(baseApiAddress, GET_ADMIN_SELA_SITES_LIST())
    },
    getK8ReportGradeBySiteId: async (siteId, grade) => {
      return _api.post(
        baseApiAddress,
        GET_K8_REPORT_GRADE_BY_SITE(siteId, grade, timezone(), PERIOD),
      )
    },
    getK8ReportGradeAndClassDetailsBySiteId: async (
      siteId,
      grade,
      classInstance,
    ) => {
      return _api.post(
        baseApiAddress,
        GET_K8_REPORT_GRADE_AND_CLASS_DETAILS_BY_SITE(
          siteId,
          grade,
          classInstance,
          timezone(),
          PERIOD,
        ),
      )
    },
    getK8ReportClassDetailsBySiteId: async (siteId, classInstance) => {
      return _api.post(
        baseApiAddress,
        GET_K8_REPORT_CLASS_DETAILS_BY_SITE(
          siteId,
          classInstance,
          timezone(),
          PERIOD,
        ),
      )
    },
    getReportsContentTrack: async licenseId => {
      return _api.post(
        baseApiAddress,
        GET_SELA_REPORT_CONTENT_TRACKS(licenseId),
      )
    },
    getReportsUsersDetail: async licenseId => {
      return _api.post(baseApiAddress, GET_SELA_REPORT_USERS_DETAILS(licenseId))
    },
    getAdminK8SitesPaginated: async (
      pageSize,
      pageNumber,
      orderBy,
      sortKey,
    ) => {
      return _api.post(
        baseApiAddress,
        GET_ADMIN_K8_SITES_PAGINATED(pageSize, pageNumber, orderBy, sortKey),
      )
    },
    getAdminK8SitesSummary: async () => {
      return _api.post(baseApiAddress, GET_ADMIN_K8_SITES_SUMMARY())
    },
    getHsUserProgressSnapshot: async (siteId, program, type) => {
      return _api.post(
        baseApiAddress,
        GET_HS_USER_PROGRESS_SNAPSHOT(siteId, program, type),
      )
    },
    getTier2DashboardData: async (adminEmail, program) => {
      const courses = getAllTier2CoursesForProgram(program)

      return _api.post(
        baseApiAddress,
        GET_TIER2_DASHBOARD_DATA(adminEmail, null, courses),
      )
    },
    getTier2LessonCompletionData: async (siteId, program, gradeBand) => {
      const courses = PROGRAM_TIER2_COURSES[program]?.[gradeBand] || []

      return _api.post(
        baseApiAddress,
        GET_TIER2_LESSON_COMPLETION_DATA(siteId, courses),
      )
    },
  }
})(baseApiAddress, true)

export default api
